/**
 * AddToPrintQueueButton — adds a QR label to the server-backed print queue.
 *
 * Accepts either a bare code id (`codeId`) or a target descriptor (`target`)
 * that the backend uses to look up / mint the QR code. Exactly one of the two
 * must be provided.
 *
 * Uses usePrintQueue().add so query invalidation (list + badge count) happens
 * automatically via the mutation's onSuccess handler.
 */

import React from 'react';
import { FaPrint } from 'react-icons/fa';
import { usePrintQueue } from '../../hooks/usePrintQueue';
import { showToast } from '../../utils/toast';

// ── Types ────────────────────────────────────────────────────────────────────

interface Props {
  /** QR code row id — post as { code_id }. */
  codeId?: string;
  /** Target descriptor — post as { target_kind, ...ids }. */
  target?: {
    target_kind: 'asset' | 'form' | 'site';
    asset_id?: string;
    form_template_id?: string;
    site_id?: string;
  };
  /** Button label override (default: "Add to print queue"). */
  label?: string;
  /** Extra className applied to the button element. */
  className?: string;
}

// ── Component ────────────────────────────────────────────────────────────────

const AddToPrintQueueButton: React.FC<Props> = ({
  codeId,
  target,
  label = 'Add to print queue',
  className = '',
}) => {
  const { add } = usePrintQueue();

  function handleClick(e: React.MouseEvent) {
    e.stopPropagation(); // prevent row-click navigation in table views
    const payload = codeId ? { code_id: codeId } : { ...target! };
    add(payload, {
      onSuccess: () => showToast('Added to print queue', 'success'),
      onError: (err) => {
        console.error('AddToPrintQueueButton: add failed', err);
        showToast('Failed to add to print queue', 'error');
      },
    });
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      title="Add to print queue"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 transition-colors ${className}`}
    >
      <FaPrint aria-hidden="true" />
      {label}
    </button>
  );
};

export default AddToPrintQueueButton;
